import { Flex, Button, Dialog, Icon } from '@servicetitan/anvil2';
import { useBreakpoint } from '@servicetitan/financing-utils-shared';
import Launch from '@servicetitan/anvil2/assets/icons/material/round/launch.svg';
import { useProviderConfig } from '@servicetitan/financing-workflow-shared';

interface SubmitButtonGroupProps {
    onCancel?: () => void;
    allowSendViaEmail?: boolean;
    onSendViaEmail?: () => void;
    onSendViaText?: () => void;
    onOpenApplication: () => void;
    onOpenPrequalification: () => void;
    submitButtonText?: string;
    showLaunchIcon?: boolean;
    isPrequalificationMode?: boolean;
    isPrequalificationLoading?: boolean;
    isApplicationLoading?: boolean;
    isSendViaEmailLoading?: boolean;
    isSendViaTextLoading?: boolean;
    showCancelButton?: boolean;
    disableTextOnlyFlow?: boolean;
}
export const SubmitButtonGroup = ({
    onCancel = () => undefined,
    allowSendViaEmail,
    onSendViaEmail = () => undefined,
    onSendViaText,
    onOpenApplication,
    onOpenPrequalification,
    submitButtonText,
    showLaunchIcon = true,
    isPrequalificationMode = false,
    isPrequalificationLoading = false,
    isApplicationLoading = false,
    isSendViaEmailLoading = false,
    isSendViaTextLoading = false,
    showCancelButton = true,
    disableTextOnlyFlow = false,
}: SubmitButtonGroupProps) => {
    const screenSize = useBreakpoint();
    const {
        applicationButtons,
        allowSendViaEmail: providerAllowsSendViaEmail = true,
        usePrequalChoiceFlow = false,
        sendViaEmailButtonText = 'Send Application Link',
    } = useProviderConfig();

    const primaryButton =
        applicationButtons === 'GetPrequalifiedAndApplyNow'
            ? 'GetPrequalified'
            : applicationButtons;
    const showApplyNowButton =
        applicationButtons === 'ApplyNow' || applicationButtons === 'GetPrequalifiedAndApplyNow';
    const showGetPrequalifiedButton =
        applicationButtons === 'GetPrequalified' ||
        applicationButtons === 'GetPrequalifiedAndApplyNow';
    const showStartPrequalButton = applicationButtons === 'StartPrequal';
    const showTextLinkButton = applicationButtons === 'TextLink';
    const isNarrowViewport = screenSize.isSm || screenSize.isMd || screenSize.isXs;

    // Special handling for prequal choice flow (e.g., Wells Fargo)
    const shouldShowPrequalChoiceFlowButton =
        usePrequalChoiceFlow &&
        !showGetPrequalifiedButton &&
        !showApplyNowButton &&
        !showStartPrequalButton;

    if (disableTextOnlyFlow && showTextLinkButton && providerAllowsSendViaEmail) {
        return null;
    }

    const shouldRenderCancelButton = showCancelButton && !isNarrowViewport;

    return (
        <Flex justifyContent="flex-end">
            <Flex gap="3" justifyContent="space-between" style={{ width: '100%' }}>
                {shouldRenderCancelButton ? (
                    <Dialog.CancelButton onClick={onCancel}>Cancel</Dialog.CancelButton>
                ) : null}
                <Flex
                    direction="column"
                    lg={{ direction: 'row-reverse' }}
                    xl={{ direction: 'row-reverse' }}
                    gap="3"
                    style={{ width: '100%' }}
                >
                    {shouldShowPrequalChoiceFlowButton && (
                        <Button
                            appearance="primary"
                            loading={
                                isPrequalificationMode
                                    ? isPrequalificationLoading
                                    : isApplicationLoading
                            }
                            onClick={() => {
                                if (isPrequalificationMode) {
                                    onOpenPrequalification();
                                } else {
                                    onOpenApplication();
                                }
                            }}
                            style={isNarrowViewport ? { width: '100%' } : {}}
                        >
                            {isPrequalificationMode
                                ? (submitButtonText ?? 'See if you prequalify')
                                : (submitButtonText ?? 'Apply Now')}
                            {(!isPrequalificationMode || showLaunchIcon) && (
                                <Icon inherit aria-hidden svg={Launch} />
                            )}
                        </Button>
                    )}
                    {showGetPrequalifiedButton && (
                        <Button
                            loading={isPrequalificationLoading}
                            appearance={
                                primaryButton === 'GetPrequalified' ? 'primary' : 'secondary'
                            }
                            onClick={() => {
                                onOpenPrequalification();
                            }}
                            style={isNarrowViewport ? { width: '100%' } : {}}
                        >
                            {isPrequalificationMode && submitButtonText
                                ? submitButtonText
                                : isPrequalificationMode
                                  ? 'See if you prequalify'
                                  : 'Get Prequalified'}
                            {showLaunchIcon && <Icon inherit aria-hidden svg={Launch} />}
                        </Button>
                    )}
                    {showStartPrequalButton && (
                        <Button
                            loading={isPrequalificationLoading}
                            appearance="primary"
                            onClick={() => {
                                onOpenPrequalification();
                            }}
                            style={isNarrowViewport ? { width: '100%' } : {}}
                        >
                            {isPrequalificationMode && submitButtonText
                                ? submitButtonText
                                : 'See if you prequalify'}
                            {showLaunchIcon && <Icon inherit aria-hidden svg={Launch} />}
                        </Button>
                    )}
                    {showTextLinkButton && (
                        <Button
                            loading={isSendViaTextLoading}
                            appearance="primary"
                            onClick={() => {
                                onSendViaText?.();
                            }}
                            style={isNarrowViewport ? { width: '100%' } : {}}
                        >
                            {submitButtonText ?? 'Text Link'}
                        </Button>
                    )}
                    {showApplyNowButton && (
                        <Button
                            loading={isApplicationLoading}
                            appearance={primaryButton === 'ApplyNow' ? 'primary' : 'secondary'}
                            style={isNarrowViewport ? { width: '100%' } : {}}
                            onClick={() => {
                                onOpenApplication();
                            }}
                        >
                            {isPrequalificationMode && submitButtonText
                                ? submitButtonText
                                : 'Apply Now'}
                            {showLaunchIcon && <Icon inherit aria-hidden svg={Launch} />}
                        </Button>
                    )}
                    {providerAllowsSendViaEmail && allowSendViaEmail && (
                        <Button
                            loading={isSendViaEmailLoading}
                            appearance="secondary"
                            style={isNarrowViewport ? { width: '100%' } : {}}
                            onClick={onSendViaEmail}
                        >
                            {sendViaEmailButtonText}
                        </Button>
                    )}
                </Flex>
            </Flex>
        </Flex>
    );
};
